import { useState, useMemo, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, CalendarDays, CalendarRange } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CalendarItem {
  id: string;
  title: string;
  date: string; // ISO date string
  [key: string]: unknown; // Allow additional properties
}

export type CalendarViewType = 'month' | '3day' | 'week';

export interface VisibleRange {
  start: Date;
  end: Date;
}

interface CalendarProps<T extends CalendarItem> {
  items: T[];
  onItemClick: (item: T) => void;
  renderItem: (item: T, index: number) => React.ReactNode;
  getDateKey: (item: T) => string | null; // Returns date key in format YYYY-MM-DD or null if no date
  getTime?: (item: T) => Date | null; // Optional: Returns start time for positioning items in time-grid views
  getEndTime?: (item: T) => Date | null; // Optional: Returns end time; if absent, events default to 60 min
  emptyMessage?: string;
  defaultView?: CalendarViewType;
  onViewTypeChange?: (viewType: CalendarViewType) => void;
  onVisibleRangeChange?: (range: VisibleRange) => void; // Called when visible date range changes
}

export function Calendar<T extends CalendarItem>({
  items,
  onItemClick,
  renderItem,
  getDateKey,
  getTime,
  getEndTime,
  emptyMessage = 'No items',
  defaultView = 'month',
  onViewTypeChange,
  onVisibleRangeChange,
}: CalendarProps<T>) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewType, setViewType] = useState<CalendarViewType>(defaultView);
  const [highlightedDateKey, setHighlightedDateKey] = useState<string | null>(null);

  const handleViewTypeChange = (newViewType: CalendarViewType) => {
    setViewType(newViewType);
    setHighlightedDateKey(null);
    onViewTypeChange?.(newViewType);
  };

  // Monthly view logic
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Get first day of month and number of days
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();

  // Group items by date
  const itemsByDate = useMemo(() => {
    const grouped = new Map<string, T[]>();
    items.forEach((item) => {
      const dateKey = getDateKey(item);
      if (dateKey) {
        if (!grouped.has(dateKey)) {
          grouped.set(dateKey, []);
        }
        grouped.get(dateKey)!.push(item);
      }
    });
    return grouped;
  }, [items, getDateKey]);

  // Multi-day view (3-day or week): get N consecutive dates starting from an anchor.
  // - 3day: starts at currentDate
  // - week: snaps to Sunday of the week containing currentDate
  const numDaysInView = viewType === 'week' ? 7 : 3;
  const multiDayDates = useMemo(() => {
    const dates: Array<{ date: Date; dateKey: string; dayName: string }> = [];
    const start = new Date(currentDate);
    if (viewType === 'week') {
      start.setDate(currentDate.getDate() - currentDate.getDay());
    }
    const dayNamesLocal = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    for (let i = 0; i < numDaysInView; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      dates.push({
        date,
        dateKey,
        dayName: dayNamesLocal[date.getDay()],
      });
    }
    return dates;
  }, [currentDate, viewType, numDaysInView]);

  // Notify parent of visible date range changes
  // Use a stable key based on view type and date values to avoid infinite loops
  const multiDayStartKey = multiDayDates[0]?.dateKey;
  const multiDayEndKey = multiDayDates[multiDayDates.length - 1]?.dateKey;

  useEffect(() => {
    if (!onVisibleRangeChange) return;

    let range: VisibleRange;
    if (viewType === 'month') {
      range = {
        start: new Date(year, month, 1),
        end: new Date(year, month + 1, 0),
      };
    } else {
      range = {
        start: multiDayDates[0].date,
        end: multiDayDates[multiDayDates.length - 1].date,
      };
    }
    onVisibleRangeChange(range);
  }, [viewType, year, month, multiDayStartKey, multiDayEndKey, onVisibleRangeChange, multiDayDates]);

  // For time-grid views: per-day layout with absolute positions and lanes for overlap.
  // Events are placed in clusters; within a cluster, each event gets the lowest free lane,
  // and the cluster's lane count determines width.
  type LaidOutItem = { item: T; startMin: number; endMin: number; lane: number; laneCount: number };
  const layoutByDate = useMemo(() => {
    if (!getTime) return new Map<string, LaidOutItem[]>();

    const byDate = new Map<string, T[]>();
    items.forEach((item) => {
      const dateKey = getDateKey(item);
      if (dateKey) {
        if (!byDate.has(dateKey)) byDate.set(dateKey, []);
        byDate.get(dateKey)!.push(item);
      }
    });

    const result = new Map<string, LaidOutItem[]>();
    byDate.forEach((dayItems, dateKey) => {
      const events = dayItems
        .map((item) => {
          const start = getTime(item);
          if (!start) return null;
          const end = getEndTime ? getEndTime(item) : null;
          const startMin = start.getHours() * 60 + start.getMinutes();
          const rawEndMin = end ? end.getHours() * 60 + end.getMinutes() : startMin + 60;
          // Ensure a minimum visible height of 20 min and clamp to end of day
          const endMin = Math.min(24 * 60, Math.max(rawEndMin, startMin + 20));
          return { item, startMin, endMin };
        })
        .filter((e): e is { item: T; startMin: number; endMin: number } => e !== null)
        .sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);

      const placed: Array<{ item: T; startMin: number; endMin: number; lane: number; clusterId: number }> = [];
      const clusterLaneCount = new Map<number, number>();
      let clusterId = -1;
      let clusterEnd = -1;

      for (const ev of events) {
        if (ev.startMin >= clusterEnd) {
          clusterId++;
          clusterEnd = ev.endMin;
          clusterLaneCount.set(clusterId, 0);
        } else {
          clusterEnd = Math.max(clusterEnd, ev.endMin);
        }
        const usedLanes = new Set(
          placed
            .filter((p) => p.clusterId === clusterId && p.endMin > ev.startMin)
            .map((p) => p.lane)
        );
        let lane = 0;
        while (usedLanes.has(lane)) lane++;
        placed.push({ ...ev, lane, clusterId });
        clusterLaneCount.set(clusterId, Math.max(clusterLaneCount.get(clusterId)!, lane + 1));
      }

      result.set(
        dateKey,
        placed.map((p) => ({
          item: p.item,
          startMin: p.startMin,
          endMin: p.endMin,
          lane: p.lane,
          laneCount: clusterLaneCount.get(p.clusterId)!,
        }))
      );
    });
    return result;
  }, [items, getDateKey, getTime, getEndTime]);

  // Create calendar days array for monthly view
  const days: Array<{ date: number; isCurrentMonth: boolean; dateKey?: string }> = [];

  // Add empty cells for days before month starts
  for (let i = 0; i < startingDayOfWeek; i++) {
    days.push({ date: 0, isCurrentMonth: false });
  }

  // Add days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    days.push({ date: day, isCurrentMonth: true, dateKey });
  }

  // Fill remaining cells to complete the grid (6 rows x 7 days = 42 cells)
  const totalCells = 42;
  const remainingCells = totalCells - days.length;
  for (let i = 0; i < remainingCells; i++) {
    days.push({ date: 0, isCurrentMonth: false });
  }

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const goToPreviousPeriod = () => {
    setHighlightedDateKey(null);
    if (viewType === 'month') {
      setCurrentDate(new Date(year, month - 1, 1));
    } else {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() - numDaysInView);
      setCurrentDate(newDate);
    }
  };

  const goToNextPeriod = () => {
    setHighlightedDateKey(null);
    if (viewType === 'month') {
      setCurrentDate(new Date(year, month + 1, 1));
    } else {
      const newDate = new Date(currentDate);
      newDate.setDate(currentDate.getDate() + numDaysInView);
      setCurrentDate(newDate);
    }
  };

  const goToToday = () => {
    setHighlightedDateKey(null);
    setCurrentDate(new Date());
  };

  const goToWeekContaining = (dateKey: string) => {
    const [y, m, d] = dateKey.split('-').map(Number);
    setCurrentDate(new Date(y, m - 1, d));
    setHighlightedDateKey(dateKey);
    setViewType('week');
    onViewTypeChange?.('week');
  };

  const todayKey = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`;

  // Generate hour slots (0-23)
  const hours = Array.from({ length: 24 }, (_, i) => i);

  // Time-grid layout constants
  const HOUR_HEIGHT = 60; // px per hour
  const DAY_HEIGHT = HOUR_HEIGHT * 24;
  const HEADER_HEIGHT = 60; // sticky header height for scroll offset

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll in time-grid views: prefer the first event of a highlighted day
  // (e.g., after clicking "+N more" from month view); otherwise scroll to "now" when today is visible.
  useEffect(() => {
    if (viewType === 'month' || !scrollContainerRef.current) return;

    let targetMin: number | null = null;

    if (highlightedDateKey && multiDayDates.some((d) => d.dateKey === highlightedDateKey)) {
      const dayEvents = layoutByDate.get(highlightedDateKey);
      if (dayEvents && dayEvents.length > 0) {
        targetMin = Math.min(...dayEvents.map((e) => e.startMin));
      }
    }

    if (targetMin === null) {
      const today = new Date();
      const todayDateKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      if (multiDayDates.some((d) => d.dateKey === todayDateKey)) {
        targetMin = today.getHours() * 60 + today.getMinutes();
      }
    }

    if (targetMin === null) return;
    const scrollTarget = targetMin;

    const timeoutId = setTimeout(() => {
      const container = scrollContainerRef.current;
      if (!container) return;
      // For a highlighted day's first event, leave a little headroom above it;
      // for "now", center it in the viewport.
      const top = highlightedDateKey
        ? Math.max(0, scrollTarget - HEADER_HEIGHT)
        : Math.max(0, scrollTarget - container.clientHeight / 2 + HEADER_HEIGHT);
      container.scrollTo({ top, behavior: 'smooth' });
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [viewType, multiDayDates, currentDate, highlightedDateKey, layoutByDate]);

  const renderMultiDayView = () => {
    const today = new Date();
    const currentDateKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const isViewingToday = multiDayDates.some((d) => d.dateKey === currentDateKey);
    const nowMin = today.getHours() * 60 + today.getMinutes();
    const gridTemplate = `60px repeat(${numDaysInView}, 1fr)`;

    return (
      <div className="rounded-lg border overflow-hidden shadow-sm">
        <div ref={scrollContainerRef} className="overflow-y-auto max-h-[calc(100vh-300px)] relative">
          {/* Day headers - sticky */}
          <div
            className="grid border-b bg-background sticky top-0 z-10 shadow-sm"
            style={{ gridTemplateColumns: gridTemplate }}
          >
            <div className="border-r bg-background" style={{ height: HEADER_HEIGHT }} />
            {multiDayDates.map(({ date, dateKey, dayName }) => {
              const isToday = dateKey === todayKey;
              const isHighlighted = dateKey === highlightedDateKey;
              return (
                <div
                  key={dateKey}
                  className={cn(
                    'border-r p-3 text-center last:border-r-0 bg-background',
                    isToday && 'bg-primary/10',
                    isHighlighted && 'bg-primary/15 ring-1 ring-inset ring-primary/40'
                  )}
                  style={{ height: HEADER_HEIGHT }}
                >
                  <div className={cn('text-sm font-semibold mb-0.5', (isToday || isHighlighted) && 'text-primary')}>
                    {dayName}
                  </div>
                  <div className={cn('text-xs text-muted-foreground', (isToday || isHighlighted) && 'text-primary/80')}>
                    {monthNames[date.getMonth()]} {date.getDate()}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Body: time gutter + day columns with absolutely-positioned events */}
          <div className="grid" style={{ gridTemplateColumns: gridTemplate }}>
            {/* Time gutter */}
            <div className="border-r bg-muted/20">
              {hours.map((hour) => (
                <div
                  key={hour}
                  className="border-b p-2 text-xs text-muted-foreground text-right font-mono"
                  style={{ height: HOUR_HEIGHT }}
                >
                  {hour.toString().padStart(2, '0')}:00
                </div>
              ))}
            </div>

            {/* Day columns */}
            {multiDayDates.map(({ dateKey }) => {
              const isToday = dateKey === todayKey;
              const isHighlighted = dateKey === highlightedDateKey;
              const dayLayout = layoutByDate.get(dateKey) || [];

              return (
                <div
                  key={dateKey}
                  className={cn(
                    'relative border-r last:border-r-0',
                    isHighlighted && 'bg-primary/5'
                  )}
                  style={{ height: DAY_HEIGHT }}
                >
                  {/* Hour gridlines + alternating bg */}
                  {hours.map((hour) => (
                    <div
                      key={hour}
                      className={cn(
                        'border-b',
                        hour % 2 === 1 && !isHighlighted && 'bg-muted/10'
                      )}
                      style={{ height: HOUR_HEIGHT }}
                    />
                  ))}

                  {/* Current time indicator */}
                  {isToday && isViewingToday && (
                    <div
                      className="absolute left-0 right-0 z-20 pointer-events-none"
                      style={{ top: nowMin }}
                    >
                      <div className="relative border-t-2 border-primary/60">
                        <div className="absolute -left-1 -top-1.5 h-3 w-3 rounded-full bg-primary border-2 border-background shadow-sm" />
                      </div>
                    </div>
                  )}

                  {/* Events */}
                  {dayLayout.map(({ item, startMin, endMin, lane, laneCount }) => (
                    <div
                      key={item.id}
                      className="absolute overflow-hidden rounded border border-border/60 bg-background shadow-sm [&>*]:h-full [&>*]:w-full"
                      style={{
                        top: startMin,
                        height: endMin - startMin,
                        left: `calc(${(lane / laneCount) * 100}% + 2px)`,
                        width: `calc(${(1 / laneCount) * 100}% - 4px)`,
                      }}
                      onClick={() => onItemClick(item)}
                    >
                      {renderItem(item, 0)}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderMonthView = () => {
    return (
      <div className="rounded-lg border overflow-hidden shadow-sm">
        <div className="overflow-y-auto max-h-[calc(100vh-300px)]">
          {/* Day headers - sticky */}
          <div className="grid grid-cols-7 border-b bg-background sticky top-0 z-10 shadow-sm">
            {dayNames.map((day) => (
              <div key={day} className="border-r p-3 text-center text-sm font-semibold text-muted-foreground last:border-r-0 bg-background">
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((day, index) => {
              const dayItems = day.dateKey ? itemsByDate.get(day.dateKey) || [] : [];
              const isToday = day.isCurrentMonth && day.dateKey === todayKey;

              return (
                <div
                  key={index}
                  className={cn(
                    'min-h-[100px] border-r border-b p-3 last:border-r-0 transition-colors',
                    !day.isCurrentMonth && 'bg-muted/20',
                    day.isCurrentMonth && !isToday && 'bg-background hover:bg-muted/10',
                    isToday && 'bg-primary/5 border-primary/20'
                  )}
                >
                  {day.isCurrentMonth && (
                    <>
                      <div className={cn('mb-2 text-sm font-semibold', isToday && 'text-primary')}>
                        {day.date}
                      </div>
                      <div className="space-y-1.5">
                        {dayItems.slice(0, 3).map((item, itemIndex) => (
                          <div key={item.id} onClick={() => onItemClick(item)}>
                            {renderItem(item, itemIndex)}
                          </div>
                        ))}
                        {dayItems.length > 3 && day.dateKey && (
                          <button
                            type="button"
                            onClick={() => goToWeekContaining(day.dateKey!)}
                            className="text-xs text-muted-foreground hover:text-primary font-medium pt-1 cursor-pointer text-left"
                          >
                            +{dayItems.length - 3} more
                          </button>
                        )}
                        {dayItems.length === 0 && day.date === new Date().getDate() && isToday && (
                          <div className="text-xs text-muted-foreground">{emptyMessage}</div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goToPreviousPeriod}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-xl font-semibold min-w-[200px] text-center">
            {viewType === 'month'
              ? `${monthNames[month]} ${year}`
              : `${multiDayDates[0].date.getDate()} ${monthNames[multiDayDates[0].date.getMonth()]} - ${multiDayDates[multiDayDates.length - 1].date.getDate()} ${monthNames[multiDayDates[multiDayDates.length - 1].date.getMonth()]} ${year}`}
          </h2>
          <Button variant="outline" size="sm" onClick={goToNextPeriod}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center rounded-md border border-input bg-background p-1">
            <Button
              variant={viewType === 'month' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => handleViewTypeChange('month')}
              className="h-8 px-2"
            >
              <CalendarDays className="h-4 w-4" />
            </Button>
            <Button
              variant={viewType === 'week' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => handleViewTypeChange('week')}
              className="h-8 px-2"
            >
              <CalendarRange className="h-4 w-4" />
            </Button>
            <Button
              variant={viewType === '3day' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => handleViewTypeChange('3day')}
              className="h-8 px-2"
            >
              <CalendarIcon className="h-4 w-4" />
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={goToToday}>
            Today
          </Button>
        </div>
      </div>

      {viewType === 'month' ? renderMonthView() : renderMultiDayView()}
    </div>
  );
}
